import React, { useEffect, useState } from 'react';
import { fetchSupplierActionItems, sortByUrgency, waitingLabel } from '../../lib/supplierActionItems';
import SupplierActionRequiredCard from './SupplierActionRequiredCard';
import { SHOW_SUPPLIER_REFERRALS } from '../../lib/featureFlags';

// Redesigned supplier Home (decision #2): opens on the WORK, not the paperwork.
// Order: action-required card (only while unverified) → needs-your-response
// (urgency = wait time) → activity → what unlocks after verification. No marketing
// footer, no onboarding stepper.
//
// 2026-09-16: the calm one-line verify nudge that used to sit below the stats was
// replaced by SupplierActionRequiredCard at the top. Registration had just opened
// to the public and nothing was pushing suppliers to finish their file. Two
// verification voices on one screen is how copy drifts apart, so there is now one.
//
// 2026-10-11 (owner): unread messages fold into ONE row — "رسالة تحتاج ردّك" /
// "N رسائل تحتاج ردّك" — instead of one row per sender showing the message
// text. The text was chat markup as often as words: a purchase-request card
// rendered as "[request:93e7…]". And a separate "available requests" card
// counts the quote requests the admin routed to this supplier that still wait
// for an offer. Mirrors the app's SupplierNeedsResponse.

const INK = '#1A1814', BRONZE = '#8B7355';

const T = {
  ar: {
    eyebrow: 'لوحة المورد', needs: 'يحتاج ردّك', activity: 'نشاطك',
    offers: 'عروض نشطة', products: 'منتجاتك', messages: 'رسائل', inquiries: 'استفسارات',
    kinds: { rfq: 'طلب عرض سعر', direct: 'شراء مباشر', sample: 'طلب عيّنة', inquiry: 'استفسار على منتج', message: 'رسالة' },
    msgsNeed: (n) => (n === 1 ? 'رسالة تحتاج ردّك' : `${n} رسائل تحتاج ردّك`),
    available: 'الطلبات المتاحة', availableSub: 'تصفّح طلبات التجار المسندة لك',
    completeProfile: 'أكمل الملف',
    unlocks: 'يفتح بعد التحقق',
    payTitle: 'المدفوعات', payBody: 'استلم قيمة طلباتك بالريال مباشرة على حسابك البنكي.',
    walletTitle: 'المحفظة والإحالات', walletBody: 'تابع أرصدتك وادعُ مصانع أخرى لمَعبر.',
  },
  en: {
    eyebrow: 'Supplier dashboard', needs: 'Needs your response', activity: 'Your activity',
    offers: 'Active offers', products: 'Your products', messages: 'Messages', inquiries: 'Inquiries',
    kinds: { rfq: 'Quote request', direct: 'Direct purchase', sample: 'Sample request', inquiry: 'Product inquiry', message: 'Message' },
    msgsNeed: (n) => (n === 1 ? '1 message needs your reply' : `${n} messages need your reply`),
    available: 'Available requests', availableSub: 'Browse the trader requests assigned to you',
    completeProfile: 'Complete profile',
    unlocks: 'Unlocks after verification',
    payTitle: 'Payments', payBody: 'Receive your order value in SAR straight to your bank account.',
    walletTitle: 'Wallet & referrals', walletBody: 'Track your balances and invite other factories to Maabar.',
  },
  zh: {
    eyebrow: '供应商面板', needs: '待您回应', activity: '您的活动',
    offers: '活跃报价', products: '您的产品', messages: '消息', inquiries: '咨询',
    kinds: { rfq: '询价', direct: '直接采购', sample: '样品申请', inquiry: '产品咨询', message: '消息' },
    msgsNeed: (n) => `${n} 条消息待您回复`,
    available: '可报价需求', availableSub: '浏览分配给您的采购需求',
    completeProfile: '完善资料',
    unlocks: '认证后解锁',
    payTitle: '收款', payBody: '订单款项以 SAR 直接到您的银行账户。',
    walletTitle: '钱包与推荐', walletBody: '查看余额并邀请其他工厂加入马巴尔。',
  },
};

export default function SupplierHomePanel({ sb, supplierId, lang = 'ar', companyName, offersCount = 0, productsCount = 0, messagesCount = 0, inquiriesCount = 0, needsVerification, showVerificationCard, profile, isUnderReview, onOpenRequests, onOpenMessages, onOpenOffers, onOpenProducts, onOpenInquiries, onVerify }) {
  const c = T[lang] || T.ar;
  const isAr = lang === 'ar';
  const font = isAr ? { fontFamily: 'var(--font-ar)' } : {};
  const [needs, setNeeds] = useState([]);
  const [msgs, setMsgs] = useState({ count: 0, since: null });
  const [availableCount, setAvailableCount] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { items } = await fetchSupplierActionItems(sb, supplierId, lang);
      if (!alive) return;
      const open = sortByUrgency(items.filter((x) => x.bucket === 'needs_response'));
      const msgItems = open.filter((x) => x.kind === 'message');
      setNeeds(open.filter((x) => x.kind !== 'message').slice(0, msgItems.length ? 2 : 3));
      // One message item per sender, so this counts conversations waiting on a reply.
      setMsgs({ count: msgItems.length, since: msgItems[0]?.waitingSince || null });
      setAvailableCount(open.filter((x) => x.kind === 'rfq').length);
    })();
    return () => { alive = false; };
  }, [sb, supplierId, lang]);

  // Every tile opens the place it counts (it opened Requests for "active
  // offers", and the other three looked like buttons and did nothing).
  const stats = [
    { v: offersCount, k: c.offers, go: onOpenOffers || onOpenRequests },
    { v: productsCount, k: c.products, go: onOpenProducts },
    { v: messagesCount, k: c.messages, go: onOpenMessages },
    { v: inquiriesCount, k: c.inquiries, go: onOpenInquiries },
  ];

  const Locked = ({ title, body, cta }) => (
    <div style={{ background: 'var(--bg-raised, #fff)', border: '1px dashed #DDD5C8', borderRadius: 14, padding: 16, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 14.5, fontWeight: 600, color: 'var(--text-disabled)', ...font }}>🔒 {title}</div>
      <p style={{ fontSize: 12.5, color: 'var(--text-disabled)', marginTop: 8, lineHeight: 1.6, ...font }}>{body}</p>
      {cta && <button onClick={onVerify} style={{ marginTop: 12, background: INK, color: '#FAF8F5', fontSize: 13, fontWeight: 600, padding: '9px 18px', borderRadius: 9, border: 'none', cursor: 'pointer', ...font }}>{c.completeProfile}</button>}
    </div>
  );

  return (
    <div style={{ maxWidth: 640 }}>
      {/* Phone only: on desktop the dashboard's own header (.sup-hdr-identity)
          already says "أهلاً، {company}", and the name twice was noise. */}
      <div className="sup-panel-heading">
        <div style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: BRONZE, marginBottom: 7, ...font }}>{c.eyebrow}</div>
        <h1 style={{ fontSize: isAr ? 22 : 26, fontWeight: 600, lineHeight: 1.35, color: 'var(--text-primary)', marginBottom: 4, overflowWrap: 'anywhere', ...font }}>{companyName}</h1>
      </div>

      {/* Action required / Unverified — top of the dashboard, before the work. */}
      {showVerificationCard && (
        <SupplierActionRequiredCard profile={profile} lang={lang} isUnderReview={isUnderReview} onVerify={onVerify} style={{ marginTop: 16 }} />
      )}

      {/* needs-your-response — dark, urgency shown as wait time */}
      {(needs.length > 0 || msgs.count > 0) && (
        <div style={{ background: INK, borderRadius: 16, padding: '16px 17px 8px', color: '#FAF8F5', marginTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12, letterSpacing: 3, textTransform: 'uppercase', color: '#C9BCA6', marginBottom: 4, ...font }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#D9C39B', flexShrink: 0 }} />{c.needs}
          </div>
          {msgs.count > 0 && (
            <button key="messages" onClick={onOpenMessages || onOpenRequests} style={{ display: 'flex', width: '100%', textAlign: isAr ? 'right' : 'left', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 0', background: 'none', border: 'none', borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: 'rgba(250,248,245,.13)', cursor: 'pointer' }}>
              <span style={{ fontSize: 14.5, fontWeight: 600, color: '#FAF8F5', lineHeight: 1.4, ...font }}>{c.msgsNeed(msgs.count)}</span>
              <span style={{ fontSize: 11.5, color: '#D9C39B', fontWeight: 600, whiteSpace: 'nowrap', ...font }}>{waitingLabel(msgs.since, lang)}</span>
            </button>
          )}
          {needs.map((it) => (
            <button key={it.key} onClick={onOpenRequests} style={{ display: 'flex', width: '100%', textAlign: isAr ? 'right' : 'left', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 0', borderBottom: '1px solid rgba(250,248,245,.13)', background: 'none', border: 'none', borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: 'rgba(250,248,245,.13)', cursor: 'pointer' }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: '#FAF8F5', lineHeight: 1.4, ...font }}>{it.title || c.kinds[it.kind]}</span>
                <span style={{ display: 'block', fontSize: 12, color: '#B5AB99', marginTop: 3, ...font }}>{c.kinds[it.kind]}</span>
              </span>
              <span style={{ fontSize: 11.5, color: '#D9C39B', fontWeight: 600, whiteSpace: 'nowrap', ...font }}>{waitingLabel(it.waitingSince, lang)}</span>
            </button>
          ))}
        </div>
      )}

      {/* available requests — what the admin routed here, still waiting for an offer */}
      <button onClick={onOpenRequests} style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 14, background: 'var(--bg-raised, #fff)', border: '1px solid var(--border-subtle)', borderRadius: 14, padding: '15px 16px', textAlign: isAr ? 'right' : 'left', cursor: 'pointer' }}>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: 'var(--text-primary)', ...font }}>{c.available}</span>
          <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-disabled)', marginTop: 4, ...font }}>{c.availableSub}</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 22, fontWeight: 500, color: 'var(--text-primary)' }}>{availableCount}</span>
          <span style={{ fontSize: 16, color: BRONZE }}>{isAr ? '←' : '→'}</span>
        </span>
      </button>

      {/* activity */}
      <div style={{ marginTop: 28 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: 'var(--text-primary)', ...font }}>{c.activity}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
          {stats.map((s, i) => (
            <button key={i} onClick={s.go} style={{ background: 'var(--bg-raised, #fff)', border: '1px solid var(--border-subtle)', borderRadius: 14, padding: '15px 16px', textAlign: isAr ? 'right' : 'left', cursor: s.go ? 'pointer' : 'default' }}>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 26, fontWeight: 500, lineHeight: 1.1, color: 'var(--text-primary)' }}>{s.v}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-disabled)', marginTop: 5, ...font }}>{s.k}</div>
            </button>
          ))}
        </div>
      </div>

      {/* what unlocks after verification (kept visible as motivation) */}
      {needsVerification && (
        <div style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: 'var(--text-primary)', ...font }}>{c.unlocks}</h2>
          <Locked title={c.payTitle} body={c.payBody} cta />
          {SHOW_SUPPLIER_REFERRALS && <Locked title={c.walletTitle} body={c.walletBody} />}
        </div>
      )}
    </div>
  );
}
